<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

interface Props {
  collapsed?: boolean
}

withDefaults(defineProps<Props>(), {
  collapsed: false
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const uiStore = useUiStore()

const userRole = computed(() => authStore.userRole)

// DEBUG: Log user role reactively
watchEffect(() => {
  console.log('[Sidebar] User role:', userRole.value, 'Auth store:', {
    role: authStore.userRole,
    isAuthenticated: authStore.isAuthenticated,
    userName: authStore.userName
  })
})

// Navigation items based on role
const navItems = computed(() => {
  const role = userRole.value

  // Patient menu - Full self-management features
  if (role === 'patient') {
    return [
      { name: 'dashboard', path: '/', icon: 'pi pi-home', label: t('nav.dashboard') },
      { name: 'glucose', path: '/glucose', icon: 'pi pi-chart-line', label: t('nav.glucose') },
      { name: 'nutrition', path: '/nutrition', icon: 'pi pi-apple', label: t('nav.nutrition') },
      { name: 'medication', path: '/medication', icon: 'pi pi-heart', label: t('nav.medication') },
      { name: 'activity', path: '/activity', icon: 'pi pi-bolt', label: t('nav.activity') },
      { name: 'problem-solving', path: '/problem-solving', icon: 'pi pi-question-circle', label: t('nav.problemSolving', 'Aide & Urgences') },
      { name: 'wellness', path: '/wellness', icon: 'pi pi-sun', label: t('nav.wellness', 'Bien-être') },
      { name: 'my-record', path: '/my-record', icon: 'pi pi-folder', label: t('nav.myRecord', 'Mon Dossier') },
      { name: 'care-team', path: '/care-team', icon: 'pi pi-id-card', label: t('nav.careTeam') },
      { name: 'telehealth', path: '/telehealth', icon: 'pi pi-video', label: t('nav.telehealth') },
      { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages'), badge: 2 },
      { name: 'education', path: '/education', icon: 'pi pi-book', label: t('nav.education') },
      { name: 'community', path: '/community', icon: 'pi pi-users', label: t('nav.community') },
      { name: 'family', path: '/family', icon: 'pi pi-heart-fill', label: t('nav.family', 'Cercle Familial') },
    ]
  }

  // Platform admin - should not render sidebar (redirected by router to /admin/)
  if (role === 'system_admin' || role === 'platform_admin') {
    return []
  }

  // Provider menu (Cabinet Indépendant - médecins indépendants)
  if (role === 'provider' || role === 'clinician') {
    return [
      { name: 'provider', path: '/provider', icon: 'pi pi-briefcase', label: t('nav.provider') },
      { name: 'provider-patients', path: '/provider/patients', icon: 'pi pi-users', label: t('provider.myPatients') },
      { name: 'provider-consultations', path: '/provider/consultations', icon: 'pi pi-file-edit', label: t('consultation.title') },
      { name: 'provider-prescriptions', path: '/provider/prescriptions', icon: 'pi pi-file', label: t('prescription.title') },
      { name: 'provider-titration', path: '/provider/titration', icon: 'pi pi-sliders-h', label: t('titration.title') },
      { name: 'provider-meal-plans', path: '/provider/meal-plans', icon: 'pi pi-apple', label: t('mealPlanning.title') },
      { name: 'provider-education', path: '/provider/education', icon: 'pi pi-book', label: t('nav.education') },
      { name: 'provider-declarations', path: '/provider/invitations', icon: 'pi pi-user-plus', label: t('provider.patientDeclarations') },
      { name: 'provider-schedule', path: '/provider/schedule', icon: 'pi pi-calendar', label: t('provider.schedule') },
      { name: 'telehealth', path: '/telehealth', icon: 'pi pi-video', label: t('nav.telehealth') },
      { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages'), badge: 3 },
    ]
  }

  // Provider admin / Clinic admin menu - Manages multiple providers
  if (['clinic_admin', 'provider_admin'].includes(role)) {
    return [
      { name: 'clinic-admin', path: '/clinic-admin', icon: 'pi pi-shield', label: t('nav.admin') },
      { name: 'patients', path: '/clinic-admin/patients', icon: 'pi pi-users', label: t('admin.patients') },
      { name: 'alerts', path: '/clinic-admin/alerts', icon: 'pi pi-exclamation-triangle', label: t('admin.alerts') },
      { name: 'telehealth', path: '/telehealth', icon: 'pi pi-video', label: t('nav.telehealth') },
      { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages'), badge: 5 },
      { name: 'education', path: '/education', icon: 'pi pi-book', label: t('nav.education') },
    ]
  }

  // Coach menu - Education and motivation focus
  if (role === 'coach') {
    return [
      { name: 'clinic-admin', path: '/clinic-admin', icon: 'pi pi-home', label: t('nav.dashboard') },
      { name: 'patients', path: '/clinic-admin/patients', icon: 'pi pi-users', label: t('admin.patients') },
      { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages'), badge: 3 },
      { name: 'education', path: '/education', icon: 'pi pi-book', label: t('nav.education') },
      { name: 'community', path: '/community', icon: 'pi pi-users', label: t('nav.community') },
    ]
  }

  // Aide-soignant menu - Basic monitoring
  if (role === 'aide_soignant') {
    return [
      { name: 'clinic-admin', path: '/clinic-admin', icon: 'pi pi-home', label: t('nav.dashboard') },
      { name: 'patients', path: '/clinic-admin/patients', icon: 'pi pi-users', label: t('admin.patients') },
      { name: 'alerts', path: '/clinic-admin/alerts', icon: 'pi pi-exclamation-triangle', label: t('admin.alerts') },
      { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages') },
    ]
  }

  // Caregiver (Aidant) menu - Monitor their patient
  if (role === 'caregiver') {
    return [
      { name: 'dashboard', path: '/', icon: 'pi pi-home', label: t('nav.dashboard') },
      { name: 'glucose', path: '/glucose', icon: 'pi pi-chart-line', label: t('nav.glucose') },
      { name: 'medication', path: '/medication', icon: 'pi pi-heart', label: t('nav.medication') },
      { name: 'telehealth', path: '/telehealth', icon: 'pi pi-video', label: t('nav.telehealth') },
      { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages') },
    ]
  }

  // Default fallback
  return [
    { name: 'dashboard', path: '/', icon: 'pi pi-home', label: t('nav.dashboard') },
    { name: 'messages', path: '/messages', icon: 'pi pi-comments', label: t('nav.messages') },
  ]
})


function isActive(path: string): boolean {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}

function navigate(path: string) {
  router.push(path)
  if (uiStore.isMobile) {
    uiStore.closeSidebar()
  }
}
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Navigation -->
    <nav class="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin">
      <!-- Main navigation -->
      <div
        v-for="item in navItems"
        :key="item.name"
        :class="[
          'nav-item cursor-pointer',
          { 'active': isActive(item.path) }
        ]"
        @click="navigate(item.path)"
      >
        <i :class="[item.icon, 'nav-item-icon']" />
        <span v-if="!collapsed" class="flex-1 hide-in-wordless">
          {{ item.label }}
        </span>
        <span
          v-if="item.badge && !collapsed"
          class="bg-red-500 text-white text-xs rounded-full px-2 py-0.5"
        >
          {{ item.badge }}
        </span>
      </div>
    </nav>

    <!-- Settings at bottom -->
    <div class="p-4 border-t border-gray-200 dark:border-gray-700">
      <div
        :class="[
          'nav-item cursor-pointer',
          { 'active': isActive('/settings') }
        ]"
        @click="navigate('/settings')"
      >
        <i class="pi pi-cog nav-item-icon" />
        <span v-if="!collapsed" class="hide-in-wordless">
          {{ t('nav.settings') }}
        </span>
      </div>
    </div>
  </div>
</template>
